import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import Modal from './Modal';
import { alertFromApiError } from '../../utils/apiAlerts';
/**
 * Shared confirmation modal. Owns open state (or accepts controlled open),
 * Cancel/Confirm actions, submitting spinner, and API error alerts.
 *
 * onConfirm: async () => void | false
 *   - resolves → shows successMessage (if set) and closes
 *   - returns false → stays open, no success alert
 *   - throws → alertFromApiError, stays open
 */
export default function ConfirmDialog({
    trigger,
    open: controlledOpen,
    onOpenChange,
    title,
    message,
    entityLabel,
    detail,
    children,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    confirmIcon,
    confirmClassName = 'btn btn-primary',
    submittingLabel = 'Updating...',
    onConfirm,
    successMessage,
    errorMessages,
}) {
    const isControlled = controlledOpen !== undefined;
    const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const isOpen = isControlled ? controlledOpen : uncontrolledOpen;

    const setOpen = (next) => {
        if (!isControlled) {
            setUncontrolledOpen(next);
        }
        onOpenChange?.(next);
    };

    const handleConfirm = async () => {
        if (isSubmitting) return;

        setIsSubmitting(true);
        try {
            const result = await onConfirm?.();
            if (result === false) {
                return;
            }
            if (successMessage && window.showAlert) {
                window.showAlert(200, successMessage);
            }
            setOpen(false);
        } catch (error) {
            console.error(error);
            alertFromApiError(error, errorMessages);
        } finally {
            setIsSubmitting(false);
        }
    };

    const form = (
        <div>
            {title && <h2>{title}</h2>}
            {message && <p>{message}</p>}
            {entityLabel && (
                <p>
                    <strong>{entityLabel}</strong>
                </p>
            )}
            {detail && <p>{detail}</p>}
            {children}
            <div className="form-actions">
                <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="btn btn-secondary"
                    disabled={isSubmitting}
                >
                    {cancelLabel}
                </button>
                <button
                    type="button"
                    onClick={handleConfirm}
                    className={confirmClassName}
                    disabled={isSubmitting}
                >
                    {isSubmitting ? (
                        <>
                            <FontAwesomeIcon icon={faSpinner} spin /> {submittingLabel}
                        </>
                    ) : (
                        <>
                            {confirmIcon ? (
                                <>
                                    <FontAwesomeIcon icon={confirmIcon} /> {confirmLabel}
                                </>
                            ) : (
                                confirmLabel
                            )}
                        </>
                    )}
                </button>
            </div>
        </div>
    );

    return (
        <>
            {trigger && (
                <span
                    className="confirm-dialog-trigger"
                    onClick={() => setOpen(true)}
                    style={{ display: 'contents' }}
                >
                    {trigger}
                </span>
            )}
            {isOpen && <Modal form={form} setIsModalOpen={setOpen} />}
        </>
    );
}
